import ResizeModule from '@dash/quill-resize-module';
import {
  Autocomplete,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material';
import { useQuill } from '@dash/react-quilljs';
import 'quill/dist/quill.snow.css';
import React, { useCallback, useEffect, useMemo } from 'react';
import { uuidv4 } from 'src/common/utils/utilities';
import { Props } from '../../container';
import { Field, FormikProps } from 'formik';
import { ScheduledEventInput, EmailAttachmentRequest } from 'src/features/reporting/store/types';
import { uploadAttachment } from 'src/features/bulk/email/send/store/services';
import { EmailTemplateTags } from '../EmailTemplateTags';
import { WO2Select } from 'src/common';
import { FormikTextField } from 'src/common/components/formik';
import ImageDropAndPaste, { ImageDataProps } from 'src/common/utils/imageDropAndPaste';
import { EmailTemplate, FromEmailAddressType, EmailAttachmentTemplate } from '../../store/types';
import { Close, InsertDriveFile } from '@mui/icons-material';

interface EmailTemplateStepProps extends Props {
  formikProps: FormikProps<ScheduledEventInput>;
}

export const EmailTemplateStep = (props: EmailTemplateStepProps) => {
  const selectedAttachments = props.formikProps.values.emailParameters?.emailAttachments ?? [];

  const { quill, quillRef, Quill } = useQuill({
    modules: {
      toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ font: ['roboto', 'notoserif', 'montserrat', 'opensans'] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, false, false, false, false, false] }],
        ['attachment'],
        ['link', 'image'],
        [{ color: [] }, { background: [] }],
        ['clean'],
      ],

      resize: {
        locale: {
          center: 'mid',
          floatLeft: 'left',
          floatRight: 'right',
          restore: 'reset',
          altTip: '',
        },
      },
      imageDropAndPaste: {
        handler: async function (quill: typeof Quill, _imageDataUrl: string, _type: string, imageData: ImageDataProps) {
          const filename = uuidv4() + '.png';
          const file = imageData.toFile(filename);
          if (file) {
            const formData = new FormData();
            formData.append('Attachment', file);
            const res = await uploadAttachment(formData);
            const index = (quill.getSelection() || {}).index;
            if (index && index < 0) {
              quill.getLength();
              quill.insertEmbed(index, 'image', res, 'user');
            }
          }
        },
      },
      clipboard: {
        matchVisual: false,
      },
    },
    formats: [
      'font',
      'bold',
      'italic',
      'underline',
      'strike',
      'align',
      'list',
      'indent',
      'size',
      'header',
      'link',
      'image',
      'color',
      'background',
      'clean',
    ],
  });

  // Register fonts & modules once Quill is available but quill instance not ready
  if (Quill && !quill) {
    const Font = Quill.import('attributors/style/font');
    Font.whitelist = ['roboto', 'notoserif', 'montserrat', 'opensans'];
    Quill.register(Font, true);
    Quill.register(
      'modules/imageDropAndPaste',
      function (
        quill: typeof Quill,
        options: {
          maxWidth: number;
          maxHeight: number;
          quality: number;
          handler: (quill: typeof Quill, dataUrl: string, type: string, data: ImageDataProps) => void;
        }
      ) {
        new ImageDropAndPaste(quill, options);
      }
    );
    Quill.register('modules/resize', ResizeModule);
    const icons = Quill.import('ui/icons');
    icons['attachment'] =
      '<svg class="MuiSvgIcon-root jss83" focusable="false" viewBox="0 0 24 24" aria-hidden="true" tabindex="-1" data-ga-event-label="AttachFileSharp" style="outline: none"><path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"></path></svg>';
  }

  const load = useCallback((quill) => {
    quill.getModule('toolbar').addHandler('image', () => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();

      input.onchange = async () => {
        if (input && input.files && input.files.length > 0) {
          const file = input.files[0];
          const body = new FormData();
          body.append('Attachment', file);
          const url = await uploadAttachment(body);
          const range = quill.getSelection();
          quill.insertEmbed(range.index, 'image', url);
        }
      };
    });
    const attachButton = document.querySelector('.ql-attachment');
    if (attachButton) {
      attachButton.addEventListener('click', () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.click();
        input.onchange = async () => {
          if (input && input.files && input.files.length > 0) {
            const file = input.files[0];
            const body = new FormData();
            body.append('Attachment', file);
            const url = await uploadAttachment(body);
            const range = quill.getSelection();
            let index = range.index;
            if (index === undefined || index === null) {
              index = 0;
            }
            quill.insertText(index, file.name, 'link', url);
          }
        };
      });
    }

    quill.on('text-change', () => {
      props.formikProps.setFieldValue('emailParameters.body', quill.root.innerHTML);
    });

    const initialContent = props.formikProps.values.emailParameters?.body;
    if (initialContent) {
      quill.root.innerHTML = initialContent;
    }
  }, []);

  const fetchEmailTemplates = () => {
    const afslIds: number[] = props.formikProps.values.clientSearchFilter?.afslIds ?? [];
    const practiceIds: number[] = props.formikProps.values.clientSearchFilter?.practiceIds ?? [];
    const adviserIds: number[] = props.formikProps.values.clientSearchFilter?.adviserIds ?? [];
    const clientIds: number[] = props.formikProps.values.clientSearchFilter?.clientIds ?? [];
    const apiAfslIds: number[] = [];
    const apiPracticeIds: number[] = [];

    const multipleAfsl = afslIds.length > 1;
    const singleAfsl = afslIds.length === 1;
    const staticClients = props.scheduler.allowStaticClients;
    const hasClientSelected = clientIds.length > 0;

    // reset the fetched templates list
    props.resetEmailTemplates();

    // primary requirement when multiple afsl provide no email template provide
    // when user manual upload static clients, no email template provide
    if (multipleAfsl || staticClients || hasClientSelected) return;

    // logic to decide which email template to fetch based on single AFSL selected
    // Single AFSL only | AFSL template provided
    // single afsl & single practice | AFSL + practice template provided
    // Single AFSL and multiple practice and single adviser provide | AFSL template
    // Multiple Advisers and single practice and single AFSL | FSL + practice template provided
    // Multiple Advisers and single AFSL | AFSL template provided.

    if (singleAfsl && practiceIds.length === 0 && adviserIds.length === 0) {
      // Single AFSL only | AFSL template provided
      apiAfslIds.push(...afslIds);
    } else if (practiceIds.length === 1 && adviserIds.length === 0) {
      // single afsl & single practice | AFSL + practice template provided
      apiAfslIds.push(...afslIds);
      apiPracticeIds.push(...practiceIds);
    } else if (practiceIds.length > 1 && singleAfsl) {
      // Single AFSL and multiple practice | AFSL template
      apiAfslIds.push(...afslIds);
    } else if (adviserIds.length > 0) {
      if (practiceIds.length === 1) {
        // Multiple Advisers and single practice and single AFSL | FSL + practice template provided
        apiAfslIds.push(...afslIds);
        apiPracticeIds.push(...practiceIds);
      } else if (singleAfsl) {
        //Multiple Advisers and single AFSL | AFSL template provided.
        apiAfslIds.push(...afslIds);
      }
    }

    // start fetching templates based on the above logic
    if (apiAfslIds.length > 0) props.fetchAfslTemplates({ entityCoreIds: apiAfslIds });
    if (apiPracticeIds.length > 0) props.fetchPracticeTemplates({ entityCoreIds: apiPracticeIds });
  };

  useEffect(() => {
    props.fetchRoles();
    fetchEmailTemplates();
  }, []);

  useEffect(() => {
    if (quill) {
      load(quill);
    }
  }, [quill, load]);

  const emailTemplates: EmailTemplate[] = useMemo(() => {
    const templates: EmailTemplate[] = [];
    props.scheduler.afslTemplates.forEach((template) => {
      templates.push({
        ...template,
        id: template.afslTemplateId,
      });
    });
    props.scheduler.practiceTemplates.forEach((template) => {
      templates.push({
        ...template,
        id: template.practiceTemplateId,
      });
    });
    return templates;
  }, [props.scheduler.afslTemplates, props.scheduler.practiceTemplates]);

  const emailAttachments: EmailAttachmentTemplate[] = useMemo(() => {
    return emailTemplates.flatMap((template) => template.attachments);
  }, [emailTemplates]);

  useEffect(() => {
    const attachments = props.formikProps.values.emailParameters?.emailAttachments ?? [];
    if (attachments.length > 0 && props.scheduler.selectedTemplate) {
      props.formikProps.setFieldValue('emailParameters.emailTemplateId', props.scheduler.selectedTemplate?.id);
    } else {
      props.formikProps.setFieldValue('emailParameters.emailTemplateId', undefined);
    }
  }, [props.formikProps.values.emailParameters?.emailAttachments, props.scheduler.selectedTemplate]);

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <FormControl fullWidth disabled={emailTemplates.length === 0}>
          <InputLabel id="email-templates">Email Template</InputLabel>
          <WO2Select
            onChange={(event) => {
              const id = event.target.value as number;
              const template = emailTemplates.find((template) => template.id === id);
              props.setSelectedTemplate(template);

              if (template) {
                props.formikProps.setFieldValue(
                  'emailParameters.emailAttachments',
                  template.attachments.map((attachment) => {
                    return {
                      attachmentId: attachment.id,
                    } as EmailAttachmentRequest;
                  })
                );
                props.formikProps.setFieldValue('emailParameters.subject', template.emailSubject);
                props.formikProps.setFieldValue('emailParameters.body', template.templateText);

                if (quill) {
                  quill.clipboard.dangerouslyPasteHTML(template.templateText);
                }
              }
            }}
            labelId="email-templates"
            label="Email Template"
            value={props.scheduler.selectedTemplate}
          >
            {emailTemplates.map((template, index) => (
              <MenuItem value={template.id} key={index}>
                {template.templateName}
              </MenuItem>
            ))}
          </WO2Select>
        </FormControl>
      </Grid>
      <Grid item>
        <Divider />
      </Grid>
      <Grid item container spacing={2}>
        <Grid item container direction="column" xs={12} lg={4}>
          <Grid item>
            <EmailTemplateTags />
          </Grid>
        </Grid>
        <Grid item container direction="column" spacing={2} xs={12} lg={8}>
          <Grid item>
            <FormControl fullWidth>
              <InputLabel id="fromEmailAddressType">From Email Address</InputLabel>
              <WO2Select
                labelId="fromEmailAddressType"
                label="From Email Address"
                value={props.formikProps.values.emailParameters?.fromEmailAddressType}
                onChange={(event) => {
                  props.formikProps.setFieldValue('emailParameters.fromEmailAddressType', event.target.value);
                }}
                required
              >
                {Object.entries(FromEmailAddressType).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </WO2Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Autocomplete
              multiple
              fullWidth
              id="cc-email-address-types"
              options={props.roles}
              value={props.roles.filter((option) =>
                (props.formikProps.values.emailParameters?.ccEmailAddressTypes || []).includes(option.name)
              )}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} label="CC Email Address" margin="normal" fullWidth />}
              onChange={(_event, selectedOptions) => {
                const selectedValues = selectedOptions.map((option) => option.name);
                props.formikProps.setFieldValue('emailParameters.ccEmailAddressTypes', selectedValues);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>
          <Grid item>
            <Field type="text" fullWidth component={FormikTextField} name="emailParameters.subject" label="Subject" />
          </Grid>
          <Grid item>
            <div ref={quillRef} style={{ minHeight: 300 }} />
          </Grid>
          <Grid item sx={{ marginTop: { xs: 8, md: 6, lg: 4 } }}>
            <List>
              {selectedAttachments.map((attachment, index) => (
                <ListItem disableGutters disablePadding key={index}>
                  <ListItemButton
                    sx={{
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      paddingY: '10px',
                    }}
                  >
                    <ListItemIcon>
                      <InsertDriveFile />
                    </ListItemIcon>
                    <ListItemText>
                      {emailAttachments.find((a) => a.id === attachment.attachmentId)?.fileName}
                    </ListItemText>
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => {
                          props.formikProps.setFieldValue(
                            'emailParameters.emailAttachments',
                            selectedAttachments.filter((_, recordIndex) => recordIndex !== index)
                          );
                        }}
                      >
                        <Close />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
